"use client"

import React from "react"

import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Star, Waves, Wind, Thermometer, Users, MapPin, TrendingUp, RefreshCw, Navigation } from "lucide-react"
import { LiveConditionsBadge } from "@/components/live-conditions-badge"
import { useSurfConditions } from "@/hooks/use-surf-conditions"
import { useInfiniteSurfSpots } from "@/hooks/use-infinite-surf-spots"
import { SpotCardImage } from "@/components/spot-card-image"
import Link from "next/link"
import { useState, useEffect, useRef, useCallback } from "react"
import { createClient } from "@/lib/supabase/client"
import { useAuth } from "@/lib/auth/context"
import { formatDistance } from "@/lib/utils/geolocation"
import { getCountryFlag } from "@/lib/utils/country-flags"

interface SurfSpotGridProps {
  userLatitude?: number | null
  userLongitude?: number | null
  maxDistance?: number | null
  sortByDistance?: boolean
}

function SurfSpotGridComponent({ userLatitude, userLongitude, maxDistance, sortByDistance }: SurfSpotGridProps) {
  const [sortBy, setSortBy] = useState<string>("rating")
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null)
  const [availableCountries, setAvailableCountries] = useState<string[]>([])
  
  const {
    spots,
    loading: spotsLoading,
    loadingMore,
    error: spotsError,
    hasMore,
    total: totalSpots,
    loadMore,
    refetch: refetchSpots,
  } = useInfiniteSurfSpots({
    userLatitude,
    userLongitude,
    maxDistance,
    sortByDistance,
    country: selectedCountry,
    pageSize: 9, // Load 9 spots at a time
  })

  // Fetch unique countries
  useEffect(() => {
    const fetchCountries = async () => {
      const supabase = createClient()
      const { data, error } = await supabase
        .from("surf_spots")
        .select("country")
        .not("country", "is", null)
      
      if (data) {
        const uniqueCountries = Array.from(new Set(data.map(item => item.country))) as string[]
        setAvailableCountries(uniqueCountries.sort())
      }
    }
    fetchCountries()
  }, [])
  
  // Intersection Observer for infinite scroll - use useCallback ref pattern
  // This ensures observer is properly re-attached when sentinel element changes
  const observerRef = useRef<IntersectionObserver | null>(null)
  
  // Ref to store latest state values for IntersectionObserver callback (avoids stale closures)
  const stateRef = useRef({ hasMore, loadingMore, spotsLoading, loadMore })
  
  // Update state ref whenever values change - this ensures observer callback always has latest values
  useEffect(() => {
    stateRef.current = { hasMore, loadingMore, spotsLoading, loadMore }
  }, [hasMore, loadingMore, spotsLoading, loadMore])
  
  // Use useCallback ref to properly manage observer lifecycle
  // This pattern disconnects old observer and attaches to new node when list updates
  const loadMoreRef = useCallback((node: HTMLDivElement | null) => {
    // Disconnect previous observer if it exists
    if (observerRef.current) {
      observerRef.current.disconnect()
      observerRef.current = null
    }
    
    // Don't attach observer if loading or no more to load
    if (loadingMore || spotsLoading || !hasMore) {
      console.log("[Infinite Scroll] Skipping observer attachment", { loadingMore, spotsLoading, hasMore })
      return
    }
    
    // Don't attach if no node
    if (!node) {
      console.log("[Infinite Scroll] No sentinel node, observer not attached")
      return
    }
    
    // Create and attach new observer
    observerRef.current = new IntersectionObserver(
      (entries) => {
        const firstEntry = entries[0]
        // Use stateRef.current to get latest values (avoids stale closure issues)
        const currentState = stateRef.current
        console.log("[Infinite Scroll] Observer callback fired", {
          isIntersecting: firstEntry?.isIntersecting,
          hasMore: currentState.hasMore,
          loadingMore: currentState.loadingMore,
          spotsLoading: currentState.spotsLoading
        })
        
        if (firstEntry?.isIntersecting && currentState.hasMore && !currentState.loadingMore && !currentState.spotsLoading) {
          console.log("[Infinite Scroll] Intersection detected, loading more...", { 
            hasMore: currentState.hasMore, 
            loadingMore: currentState.loadingMore, 
            spotsLoading: currentState.spotsLoading,
            currentSpotsCount: spots.length
          })
          currentState.loadMore()
        }
      },
      {
        root: null, // Use viewport as root
        rootMargin: "300px", // Start loading 300px before reaching the bottom
        threshold: 0.1, // Fire when 10% of element is visible
      }
    )
    
    observerRef.current.observe(node)
    console.log("[Infinite Scroll] Observer attached to sentinel element", {
      hasMore,
      spotsCount: spots.length,
      nodeHeight: node.offsetHeight,
      nodeVisible: node.offsetParent !== null
    })
  }, [hasMore, loadingMore, spotsLoading, loadMore, spots.length])
  
  // Fetch conditions using the forecast API for each spot (same source as details page)
  // Only fetch conditions for visible spots (first 60 spots to avoid too many requests)
  const visibleSpots = spots.slice(0, 60)
  const { conditions, loading: conditionsLoading, error: conditionsError, refetch } = useSurfConditions(visibleSpots)
  const { user } = useAuth()
  const [favoritedSpots, setFavoritedSpots] = useState<Set<string>>(new Set())
  const [togglingFavorite, setTogglingFavorite] = useState<string | null>(null)
  const [userProfileId, setUserProfileId] = useState<string | null>(null)

  const loading = spotsLoading || conditionsLoading
  const error = spotsError || conditionsError
  
  // Debug: Log conditions loading state and spot ID matching
  useEffect(() => {
    if (conditionsError) {
      console.warn("[SurfSpotGrid] Conditions error:", conditionsError)
    }
    if (conditions && Object.keys(conditions).length > 0) {
      console.log("[SurfSpotGrid] Conditions loaded for spots:", Object.keys(conditions).length)
      console.log("[SurfSpotGrid] Condition spot IDs:", Object.keys(conditions))
      console.log("[SurfSpotGrid] Current spot IDs:", spots.slice(0, 9).map(s => s.id))
      
      // Check if any spots have matching conditions
      const matchingSpots = spots.filter(spot => conditions[spot.id])
      console.log("[SurfSpotGrid] Spots with matching conditions:", matchingSpots.length, matchingSpots.map(s => s.name))
    }
  }, [conditions, conditionsError, conditionsLoading, spots])

  // Cleanup observer on unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
        observerRef.current = null
        console.log("[Infinite Scroll] Observer cleaned up on unmount")
      }
    }
  }, [])

  // Fetch user profile ID from users table (same approach as dashboard)
  useEffect(() => {
    const fetchUserProfileId = async () => {
      if (!user?.id) return
      
      const supabase = createClient()
      try {
        const { data, error } = await supabase
          .from("users")
          .select("id")
          .eq("auth_id", user.id)
          .single()
        
        if (error) {
          console.error("Error fetching user profile ID:", error)
          return
        }
        
        if (data) {
          setUserProfileId(data.id)
        }
      } catch (error) {
        console.error("Error fetching user profile ID:", error)
      }
    }
    
    if (user) {
      fetchUserProfileId()
    }
  }, [user])

  useEffect(() => {
    if (userProfileId) {
      fetchUserFavorites()
    }
  }, [userProfileId])

  const fetchUserFavorites = async () => {
    if (!userProfileId) return

    const supabase = createClient()
    try {
      const { data, error } = await supabase.from("favorites").select("surf_spot_id").eq("user_id", userProfileId)

      if (error) {
        console.error("Error fetching user favorites:", error)
        return
      }

      const favoritedSpotIds = new Set(data?.map((fav) => fav.surf_spot_id) || [])
      setFavoritedSpots(favoritedSpotIds)
    } catch (error) {
      console.error("Error fetching user favorites:", error)
    }
  }

  const toggleFavorite = async (spotId: string, e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    if (!userProfileId) {
      console.error("User profile not found")
      return
    }

    setTogglingFavorite(spotId)
    const supabase = createClient()
    const isFavorited = favoritedSpots.has(spotId)

    try {
      if (isFavorited) {
        const { error } = await supabase
          .from("favorites")
          .delete()
          .eq("user_id", userProfileId)
          .eq("surf_spot_id", spotId)

        if (error) {
          console.error("Error removing favorite:", error)
          return
        }

        setFavoritedSpots((prev) => {
          const newSet = new Set(prev)
          newSet.delete(spotId)
          return newSet
        })
      } else {
        const { error } = await supabase.from("favorites").insert({
          user_id: userProfileId,
          surf_spot_id: spotId,
        })

        if (error) {
          console.error("Error adding favorite:", error)
          return
        }

        setFavoritedSpots((prev) => new Set(prev).add(spotId))
      }
    } catch (error) {
      console.error("Error toggling favorite:", error)
    } finally {
      setTogglingFavorite(null)
    }
  }

  const getDifficultyDisplay = (skillLevel: string | null) => {
    if (!skillLevel) return "Unknown"
    return skillLevel.charAt(0).toUpperCase() + skillLevel.slice(1)
  }

  const getCrowdLevel = (rating: number) => {
    if (rating >= 4.5) return "High"
    if (rating >= 3.5) return "Medium"
    return "Low"
  }

  const generateTags = (spot: any) => {
    const tags = []
    if (spot.wave_type) {
      tags.push(spot.wave_type.replace("-", " ").replace(/\b\w/g, (l: string) => l.toUpperCase()))
    }
    if (spot.avg_overall_rating >= 4.5) tags.push("Highly Rated")
    if (spot.review_count > 10) tags.push("Popular")
    return tags.slice(0, 3) // Limit to 3 tags
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="mb-2">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-4 gap-4 px-4 pt-4 lg:px-6">
          <div>
            <h2 className="text-xl lg:text-2xl font-black text-slate-900 dark:text-white uppercase tracking-tight">
              {sortByDistance ? "Surf Spots Near You" : "Discover Surf Spots"}
            </h2>
            <p className="text-sm lg:text-base text-slate-500 dark:text-slate-400 font-medium">
              {sortByDistance
                ? `Found ${spots.length} surf spots within your selected radius`
                : `Explore ${totalSpots ?? spots.length} world-class surf spots with real-time conditions and detailed forecasts`}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 lg:gap-3">
            <Badge
              variant="secondary"
              className="bg-blue-50 text-blue-600 dark:bg-blue-900/40 dark:text-blue-400 w-fit px-3 py-1 border border-blue-100 dark:border-blue-900/50"
            >
              <TrendingUp className="w-3.5 h-3.5 mr-1.5" />
              Live Conditions
            </Badge>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={refetchSpots}
                disabled={loading}
                haptic={true}
                className="border-blue-200 text-blue-600 hover:bg-blue-50 bg-white dark:bg-slate-900 dark:border-blue-800 dark:text-blue-400 dark:hover:bg-blue-900/20 shadow-sm"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </Button>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[180px] bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
                  <SelectValue placeholder="Sort by..." />
                </SelectTrigger>
                <SelectContent className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
                  <SelectItem value="rating">Sort by Rating</SelectItem>
                  <SelectItem value="distance">Sort by Distance</SelectItem>
                  <SelectItem value="popularity">Sort by Popularity</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Country Filter Bar - visible only on mobile */}
        <div className="flex flex-wrap gap-2 mb-6 md:hidden">
          <Button
            variant={selectedCountry === null ? "default" : "outline"}
            size="sm"
            onClick={() => setSelectedCountry(null)}
            className={`rounded-2xl h-10 px-5 text-[10px] font-bold uppercase tracking-wider transition-all duration-300 ${
              selectedCountry === null 
                ? "bg-blue-600 text-white hover:bg-blue-700 shadow-md shadow-blue-500/20 border-0" 
                : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-500 hover:text-blue-600 hover:border-blue-200 shadow-sm"
            }`}
          >
            All Countries
          </Button>
          {availableCountries.map((country) => (
            <Button
              key={country}
              variant={selectedCountry === country ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedCountry(country)}
              className={`rounded-2xl h-10 px-5 text-[10px] font-bold uppercase tracking-wider transition-all duration-300 ${
                selectedCountry === country
                  ? "bg-blue-600 text-white hover:bg-blue-700 border-0 shadow-md shadow-blue-500/20"
                  : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-500 hover:text-blue-600 hover:border-blue-200 shadow-sm"
              }`}
            >
              {country}
            </Button>
          ))}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
            <p className="text-sm">Failed to load data: {error}</p>
          </div>
        )}
      </div>

      {/* Loading State */}
      {loading && spots.length === 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4 lg:gap-6">
          {[...Array(6)].map((_, i) => (
            <Card key={i} className="bg-white dark:bg-slate-800 overflow-hidden border shadow-lg">
              <div className="h-40 sm:h-48 bg-muted animate-pulse" />
              <CardContent className="p-3 lg:p-4 space-y-3">
                <div className="h-4 bg-muted rounded animate-pulse" />
                <div className="h-3 bg-muted rounded animate-pulse w-3/4" />
                <div className="h-16 bg-muted rounded animate-pulse" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Grid */}
      {!loading || spots.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4 lg:gap-6">
          {spots.map((spot) => {
            const liveConditions = conditions?.[spot.id]
            // Debug: Log condition lookup for first few spots
            if (spots.indexOf(spot) < 3) {
              console.log(`[SurfSpotGrid] Spot ${spot.name} (${spot.id}):`, {
                hasConditions: !!liveConditions,
                conditionsLoading,
                willRenderBadge: !!(liveConditions && !conditionsLoading),
                conditionsKeys: conditions ? Object.keys(conditions) : [],
                spotId: spot.id,
                liveConditionsData: liveConditions ? {
                  surfQuality: liveConditions.surfQuality,
                  waveHeight: liveConditions.waveHeight,
                  windSpeed: liveConditions.windSpeed
                } : null
              })
            }
            const tags = generateTags(spot)
            const isFavorited = favoritedSpots.has(spot.id)
            const isToggling = togglingFavorite === spot.id

            return (
              <Card
                key={spot.id}
                className="group hover:shadow-2xl transition-all duration-300 shadow-lg drop-shadow-md hover:shadow-blue-300/60 dark:hover:shadow-blue-800/40 bg-white dark:bg-slate-800 text-card-foreground backdrop-blur-sm overflow-hidden border-0 flex flex-col"
              >
                <div className="relative">
                  <Link href={`/spot/${spot.slug}`} scroll={false} className="block">
                    <SpotCardImage spotId={spot.id} spotName={spot.name} fallbackUrl={spot.image_url} />
                  </Link>
                  {/* Country Flag - Top Left */}
                  <div className="absolute top-3 left-3 bg-black/60 backdrop-blur-sm rounded-full p-1.5 flex items-center justify-center z-10">
                    <span className="text-lg leading-none">{getCountryFlag(spot.country)}</span>
                  </div>
                  {/* Top Rated Badge - Positioned to the right of flag */}
                  {spot.avg_overall_rating >= 4.5 && (
                    <Badge className="absolute top-3 left-14 bg-orange-500 text-white text-xs z-10">
                      <TrendingUp className="w-3 h-3 mr-1" />
                      Top Rated
                    </Badge>
                  )}
                  {/* Distance Badge - Positioned below flag or to the right if Top Rated exists */}
                  {spot.distance !== undefined && (
                    <Badge className={`absolute ${spot.avg_overall_rating >= 4.5 ? 'top-3 left-32' : 'top-3 left-14'} bg-blue-500 text-white text-xs font-semibold z-10`}>
                      <Navigation className="w-3 h-3 mr-1" />
                      {formatDistance(spot.distance)}
                    </Badge>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={(e) => toggleFavorite(spot.id, e)}
                    disabled={isToggling || !user}
                    className="absolute top-3 right-3 bg-black/20 hover:bg-black/30 backdrop-blur-sm disabled:opacity-50"
                    aria-label={isFavorited ? "Remove from favorites" : "Add to favorites"}
                  >
                    <Star
                      className={`w-6 h-6 transition-colors ${
                        isFavorited ? "fill-yellow-400 text-yellow-400" : "text-white hover:text-yellow-400"
                      }`}
                    />
                  </Button>
                  {/* Activity Type Icons - Bottom Left */}
                  {((spot as any)?.activity_type && (
                    <div className="absolute bottom-3 left-3 flex items-center gap-1.5">
                      {((spot as any).activity_type === "surfing" || (spot as any).activity_type === "both") && (
                        <div className="bg-black/60 backdrop-blur-sm rounded-full p-1.5 flex items-center justify-center">
                          <span className="text-xl">🏄</span>
                        </div>
                      )}
                      {((spot as any).activity_type === "kitesurfing" || (spot as any).activity_type === "both") && (
                        <div className="bg-black/60 backdrop-blur-sm rounded-full p-1.5 flex items-center justify-center">
                          <span className="text-xl">🪁</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <CardContent className="p-3 lg:p-4 flex flex-col flex-1">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      <Link href={`/spot/${spot.slug}`} scroll={false}>
                        <h3 className="font-bold text-base lg:text-lg text-foreground group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors truncate cursor-pointer hover:underline">
                          {spot.name}
                        </h3>
                      </Link>
                      <p className="text-sm text-muted-foreground flex items-center gap-1 truncate">
                        <MapPin className="w-3 h-3 flex-shrink-0" />
                        {spot.location}
                        {spot.distance !== undefined && (
                          <span className="text-blue-600 dark:text-blue-400 font-medium ml-1">
                            • {formatDistance(spot.distance)} away
                          </span>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                      <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      <span className="font-semibold text-foreground text-sm">
                        {spot.avg_overall_rating > 0 ? spot.avg_overall_rating.toFixed(1) : "N/A"}
                      </span>
                    </div>
                  </div>

                  <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                    {spot.description ||
                      `Experience the waves at ${spot.name}, a ${spot.wave_type || "surf"} spot in ${spot.country}.`}
                  </p>

                  {liveConditions ? (
                    <LiveConditionsBadge
                      quality={liveConditions.surfQuality}
                      waveHeight={liveConditions.waveHeight}
                      windSpeed={liveConditions.windSpeed}
                      waterTemp={liveConditions.waterTemp}
                      lastUpdated={liveConditions.lastUpdated}
                      kiteQuality={liveConditions.kiteQuality}
                      activityType={(spot as any)?.activity_type || null}
                      className="mb-3"
                    />
                  ) : conditionsLoading ? (
                    <div className="mb-3 space-y-2">
                      <div className="h-4 bg-muted rounded animate-pulse"></div>
                      <div className="h-3 bg-muted rounded animate-pulse w-3/4"></div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      <div className="flex items-center gap-1 text-xs">
                        <Waves className="w-3 h-3 text-blue-500" />
                        <span className="text-muted-foreground">--</span>
                      </div>
                      <div className="flex items-center gap-1 text-xs">
                        <Wind className="w-3 h-3 text-gray-500" />
                        <span className="text-muted-foreground">--</span>
                      </div>
                      <div className="flex items-center gap-1 text-xs">
                        <Thermometer className="w-3 h-3 text-orange-500" />
                        <span className="text-muted-foreground">--</span>
                      </div>
                    </div>
                  )}

                  {/* Tags */}
                  {tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {tags.map((tag, index) => (
                        <Badge
                          key={index}
                          variant="secondary"
                          className="text-[10px] bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400 border border-blue-100/50 dark:border-blue-800/50 px-2 py-0.5 rounded-lg"
                        >
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}

                  {/* Difficulty & Stats */}
                  <div className="flex items-center justify-between text-xs text-muted-foreground mb-3">
                    <span className="flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      {getDifficultyDisplay(spot.skill_level)}
                    </span>
                    <div className="flex items-center gap-3">
                      <span>Reviews: {spot.review_count}</span>
                      {spot.session_count !== undefined && spot.session_count > 0 && (
                        <span>Sessions: {spot.session_count}</span>
                      )}
                    </div>
                  </div>

                  {/* Push button to bottom */}
                  <div className="mt-auto pt-2">
                    <Link href={`/spot/${spot.slug}`} scroll={false}>
                      <Button haptic={true} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-2xl shadow-md shadow-blue-500/20 transition-all">
                        View Details
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>
      ) : null}

      {/* Infinite Scroll Sentinel & Loading More Indicator */}
      {/* Always render sentinel when we have spots, even if hasMore is false (to show end message) */}
      {!loading && spots.length > 0 && (
        <div 
          ref={loadMoreRef}
          className="mt-8 flex flex-col items-center justify-center py-8 min-h-[100px]"
          style={{ minHeight: '100px' }} // Ensure element has height for intersection detection
        >
          {loadingMore && (
            <div className="flex flex-col items-center gap-3">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              <p className="text-sm text-muted-foreground">Loading more spots...</p>
            </div>
          )}
          {!hasMore && !loadingMore && (
            <p className="text-sm text-muted-foreground">You've reached the end! 🏄</p>
          )}
          {hasMore && !loadingMore && (
            <p className="text-sm text-muted-foreground opacity-50">Scroll down for more spots...</p>
          )}
        </div>
      )}
    </div>
  )
}

// Memoize component to prevent unnecessary re-renders when props haven't changed
export const SurfSpotGrid = React.memo(SurfSpotGridComponent, (prevProps, nextProps) => {
  return (
    prevProps.userLatitude === nextProps.userLatitude &&
    prevProps.userLongitude === nextProps.userLongitude &&
    prevProps.maxDistance === nextProps.maxDistance &&
    prevProps.sortByDistance === nextProps.sortByDistance
  )
})